import { HttpContext } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ManagementMainPanelTitleComponent } from '@connect-services/client/components/management/management-main-panel-title/management-main-panel-title.component';
import { BYPASS_INTERCEPTOR } from '@connect-services/client/interceptors/http-errors.interceptor';
import {
    PasswordRules,
    PasswordRulesControllerPatchRequestParams,
    PasswordRulesService,
} from '@connect-services/client/sdk/api';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { ToggleComponent } from '@shared/client/components/toggle/toggle.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export interface PasswordRulesForm {
    minLength: FormControl<number | null>;
    minNumbers: FormControl<number | null>;
    minSpecialCharacters: FormControl<number | null>;
    minUppercaseCharacters: FormControl<number | null>;
    sendMail: FormControl<boolean>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CheckboxComponent,
        InputNumberComponent,
        LoadingComponent,
        ManagementMainPanelTitleComponent,
        RessourcesModule,
        ToggleComponent,
    ],
    selector: 'app-password-rules',
    templateUrl: './password-rules.component.html',
})
export class PasswordRulesComponent implements OnInit {
    protected form!: FormGroup<PasswordRulesForm>;
    protected readonly isLoading = signal<boolean>(true);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly CheckboxState = CheckboxState;

    private hadPasswordRules: boolean = false;
    private organizationId!: string;

    private readonly passwordRulesService = inject(PasswordRulesService);
    private readonly router = inject(Router);
    private readonly securityService = inject(SecurityService);
    private readonly snackBarService = inject(SnackBarService);

    public ngOnInit(): void {
        const organizationId = this.securityService.meSignal()?.organization.id;
        if (!organizationId) {
            this.snackBarService.openErrorSnackBar('errorForbidden');
            void this.router.navigate(['/']);

            return;
        }
        this.organizationId = organizationId;
        void this.initForm();
    }

    protected setControlValue(control: FormControl<number | null>, value: boolean): void {
        control.setValue(value ? 1 : null);
        this.form.markAsDirty();
    }

    protected async submitForm(): Promise<void> {
        if (this.form.invalid || !this.form.dirty) {
            return;
        }

        const formValue = this.form.getRawValue();

        const requestParameters: [PasswordRulesControllerPatchRequestParams, 'response', boolean, object] = [
            {
                id: this.organizationId,
                updatePasswordRulesDto: formValue,
            },
            'response',
            true,
            {
                context: new HttpContext().set(BYPASS_INTERCEPTOR, ['httpErrorsInterceptor']),
            },
        ];

        try {
            if (this.hadPasswordRules) {
                await firstValueFrom(this.passwordRulesService.passwordRulesControllerPatch(...requestParameters));
            } else {
                await firstValueFrom(this.passwordRulesService.passwordRulesControllerPost(...requestParameters));
            }
            this.snackBarService.openSuccessSnackBar('passwordRules.updateSuccess');
            void this.initForm();
        } catch {
            this.snackBarService.openErrorSnackBar('passwordRules.updateError');
        }
    }

    protected toggleSendMailControlValue(): void {
        this.form.controls.sendMail.setValue(!this.form.controls.sendMail.value);
        this.form.markAsDirty();
    }

    private async getPasswordRules(): Promise<PasswordRules | null> {
        try {
            return await firstValueFrom(
                this.passwordRulesService.passwordRulesControllerGetOne({ id: this.organizationId }),
            );
        } catch {
            return Promise.resolve(null);
        }
    }

    private async initForm(): Promise<void> {
        const currentPasswordRules = await this.getPasswordRules();
        this.hadPasswordRules = !!currentPasswordRules;

        this.form = new FormGroup<PasswordRulesForm>({
            minLength: new FormControl<number | null>(currentPasswordRules?.minLength ?? null, {
                validators: [Validators.min(1)],
            }),
            minNumbers: new FormControl<number | null>(currentPasswordRules?.minNumbers ?? null, {
                validators: [Validators.min(1)],
            }),
            minSpecialCharacters: new FormControl<number | null>(currentPasswordRules?.minSpecialCharacters ?? null, {
                validators: [Validators.min(1)],
            }),
            minUppercaseCharacters: new FormControl<number | null>(
                currentPasswordRules?.minUppercaseCharacters ?? null,
                {
                    validators: [Validators.min(1)],
                },
            ),
            sendMail: new FormControl<boolean>(false, { nonNullable: true }),
        });

        this.isLoading.set(false);
    }
}
